import { useMemo } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { DetailList, type DetailRow } from '../../components/ui/DetailList';
import { StatusPill } from '../../components/ui/StatusPill';
import { useAgentDetail } from './agentForm';
import { formatCount, formatDate, formatDateTime } from '../../utils/format';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

/** Values arrive already expressed in the agent's own unit, so the API's unit
 *  label is appended verbatim rather than converted. */
const formatMeasure = (value: number | null, unit: string): string =>
  value == null ? '' : `${value} ${unit}`.trim();

/** Read-only agent profile: identity, persona and behaviour, and activity. */
export function AgentProfileTab() {
  const { t } = useTranslation('admin');
  const { agent } = useAgentDetail();

  const agentRows = useMemo<readonly DetailRow[]>(
    () => [
      {
        label: t('agents.details.status'),
        value: (
          <StatusPill
            label={agent.isActive ? t('status.active') : t('status.inactive')}
            tone={agent.isActive ? 'success' : 'neutral'}
          />
        ),
      },
      { label: t('agents.details.persona'), value: agent.persona },
      { label: t('agents.details.tone'), value: agent.tone },
      { label: t('agents.details.motivationStyle'), value: agent.motivationStyle },
      { label: t('agents.details.email'), value: agent.email },
      {
        label: t('agents.details.emailVerified'),
        value: agent.isEmailVerified ? t('common.yes') : t('common.no'),
      },
      { label: t('agents.details.mobileNo'), value: agent.mobileNo },
      { label: t('agents.details.country'), value: agent.country },
      { label: t('agents.details.gender'), value: agent.gender },
      { label: t('agents.details.dateOfBirth'), value: formatDate(agent.dateOfBirth) },
      {
        label: t('agents.details.height'),
        value: formatMeasure(agent.currentHeight, agent.heightUnit),
      },
      {
        label: t('agents.details.currentWeight'),
        value: formatMeasure(agent.currentWeight, agent.weightUnit),
      },
      {
        label: t('agents.details.startWeight'),
        value: formatMeasure(agent.startWeight, agent.weightUnit),
      },
      {
        label: t('agents.details.goalWeight'),
        value: formatMeasure(agent.goalWeight, agent.weightUnit),
      },
      {
        label: t('agents.details.likeFrequency'),
        value: formatCount(agent.likeFrequency),
      },
      {
        label: t('agents.details.commentFrequency'),
        value: formatCount(agent.commentFrequency),
      },
      { label: t('agents.details.created'), value: formatDateTime(agent.joiningDate) },
      { label: t('agents.details.bio'), value: agent.bio },
    ],
    [t, agent],
  );

  const activityRows = useMemo<readonly DetailRow[]>(
    () => [
      { label: t('agents.details.totalPosts'), value: formatCount(agent.totalPosts) },
      { label: t('agents.details.weeklyPosts'), value: formatCount(agent.weeklyPosts) },
      { label: t('agents.details.weeklyLikes'), value: formatCount(agent.weeklyLikes) },
      {
        label: t('agents.details.weeklyComments'),
        value: formatCount(agent.weeklyComments),
      },
      { label: t('agents.details.buddiesCount'), value: formatCount(agent.buddiesCount) },
      { label: t('agents.details.firstPost'), value: formatDate(agent.firstPostDate) },
      { label: t('agents.details.lastPost'), value: formatDate(agent.lastPostDate) },
    ],
    [t, agent],
  );

  return (
    <div className="page__section">
      <section className="page__panel" aria-labelledby="agent-profile-heading">
        <h2 className="ui-section-title" id="agent-profile-heading">
          {t('agents.details.sections.account')}
        </h2>
        <DetailList rows={agentRows} />
      </section>

      <section className="page__panel" aria-labelledby="agent-activity-heading">
        <h2 className="ui-section-title" id="agent-activity-heading">
          {t('agents.details.sections.activity')}
        </h2>
        <DetailList rows={activityRows} />
      </section>
    </div>
  );
}
